/**
 * Local dev-only mock Cognito session (no AWS calls).
 *
 * Env (CRA): set in .env.local
 *   REACT_APP_MOCK_AUTH=true
 *   REACT_APP_MOCK_GROUPS=web-page          (comma-separated Cognito group names; omit super-admin to test non-admin)
 *   REACT_APP_MOCK_USERNAME=                (optional; if set, only this email may sign in)
 *   REACT_APP_MOCK_PASSWORD=                (optional; if set, password must match)
 *
 * Mock auth is disabled when NODE_ENV !== 'development' (production builds never use it).
 */

const MOCK_FLAG = 'pih_mock_auth';
const MOCK_USERNAME_KEY = 'pih_mock_username';

function base64UrlEncode(str) {
  const base64 = btoa(unescape(encodeURIComponent(str)));
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function createMockJwt(payload) {
  const header = base64UrlEncode(JSON.stringify({ alg: 'none', typ: 'JWT' }));
  const body = base64UrlEncode(JSON.stringify(payload));
  return `${header}.${body}.pih-mock`;
}

export function isMockAuthEnabled() {
  return process.env.REACT_APP_MOCK_AUTH === 'true' && process.env.NODE_ENV === 'development';
}

export function isMockSessionActive() {
  return typeof localStorage !== 'undefined' && localStorage.getItem(MOCK_FLAG) === '1';
}

export function getMockGroupsFromEnv() {
  const raw = process.env.REACT_APP_MOCK_GROUPS || 'web-page';
  return raw
    .split(',')
    .map((g) => g.trim())
    .filter(Boolean);
}

export function validateMockCredentials(username, password) {
  const normalized = username?.trim().toLowerCase();
  const expectedUser = process.env.REACT_APP_MOCK_USERNAME?.trim().toLowerCase();
  if (expectedUser && normalized !== expectedUser) {
    const err = new Error('Incorrect username or password');
    err.code = 'NotAuthorizedException';
    throw err;
  }
  const expectedPass = process.env.REACT_APP_MOCK_PASSWORD;
  if (expectedPass != null && expectedPass !== '' && password !== expectedPass) {
    const err = new Error('Incorrect username or password');
    err.code = 'NotAuthorizedException';
    throw err;
  }
  if (!password) {
    const err = new Error('Incorrect username or password');
    err.code = 'NotAuthorizedException';
    throw err;
  }
}

function mockSubForUsername(username) {
  return `mock-sub-${base64UrlEncode(username).slice(0, 24)}`;
}

/**
 * @param {string} username
 * @returns {object} Cognito-like user object for signIn / setTokens / sidebar
 */
export function buildMockCognitoUser(username) {
  const normalized = username?.trim().toLowerCase() || 'dev@local.test';
  const groups = getMockGroupsFromEnv();
  const sub = mockSubForUsername(normalized);
  const now = Math.floor(Date.now() / 1000);
  const exp = now + 60 * 60 * 24 * 365;

  const idPayload = {
    sub,
    email: normalized,
    exp,
    iat: now,
    token_use: 'id',
  };
  const accessPayload = {
    sub,
    exp,
    iat: now,
    token_use: 'access',
    'cognito:groups': groups,
  };

  const idTokenJwt = createMockJwt(idPayload);
  const accessTokenJwt = createMockJwt(accessPayload);

  const signInUserSession = {
    idToken: { jwtToken: idTokenJwt, payload: idPayload },
    accessToken: { jwtToken: accessTokenJwt, payload: accessPayload },
    refreshToken: { token: 'pih-mock-refresh' },
    getIdToken() {
      return {
        getJwtToken: () => idTokenJwt,
        payload: idPayload,
      };
    },
  };

  return {
    __pihMockAuth: true,
    username: normalized,
    attributes: { email: normalized },
    signInUserSession,
  };
}

export function persistMockSessionMarker(username) {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(MOCK_FLAG, '1');
  localStorage.setItem(MOCK_USERNAME_KEY, username?.trim().toLowerCase() || '');
}

export function clearMockSessionMarker() {
  if (typeof localStorage === 'undefined') return;
  localStorage.removeItem(MOCK_FLAG);
  localStorage.removeItem(MOCK_USERNAME_KEY);
}

export function getStoredMockUsername() {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem(MOCK_USERNAME_KEY);
}
